import React, { useCallback, useEffect, useMemo, useState } from 'react'
import IconLock from '~/assets/images/lock-white.svg'
import { useTranslation } from 'react-i18next'
import { useGA, useIsValidNetwork } from '~/hooks'
import { useModalOpened, useModalToggle } from '~/state/application/hooks'
import { ApplicationModal } from '~/state/application/reducer'
import {
  useLimitOrderUserConfig,
  useLimitSwapAmount,
  useLimitSwapPair,
  useIsDisableTrade,
} from '~/state/dex/hooks'
import { useNeedApprove } from '../../hooks/useNeedApprove'
import { useTradeSubmit } from '../../hooks/useTradeSubmit'
import { useTradeVerify } from '../../hooks/useTradeVerify'
import { StyledTradeButton } from './styled'
import SpreadWarningCheck from '../SpreadWarningCheck'
import { useUSDValueSpreadCallback } from '../SpreadLabel'
import { useAccount } from 'wagmi'
import NotificationBar from '~/components/NotificationBar'
import Space from '~/components/Space'

const TradeButton: React.FC<{
  text?: string
  setTipVisible?: (value: boolean) => void
}> = ({ text, setTipVisible }) => {
  const { trackClick } = useGA()
  const { t } = useTranslation()
  const { address: account } = useAccount()
  const isValidNetwork = useIsValidNetwork()
  const isDisableTrade = useIsDisableTrade()
  const isPreviewOpen = useModalOpened(ApplicationModal.LIMIT_ORDER_PREVIEW)
  const togglePreviewModal = useModalToggle(
    ApplicationModal.LIMIT_ORDER_PREVIEW,
  )
  const toggleWalletModal = useModalToggle(ApplicationModal.WALLET_CONNECT, {
    modalState: { from: 'limit_order' },
  })
  const toggleSwitchNetworkModal = useModalToggle(
    ApplicationModal.SWITCH_NETWORK,
  )
  const toggleIpBlockedModal = useModalToggle(ApplicationModal.IP_BLOCKED)

  const [inputToken, outputToken] = useLimitSwapPair()
  const { inputAmount, outputAmount } = useLimitSwapAmount()
  const { expiration } = useLimitOrderUserConfig()
  const needApprove = useNeedApprove()
  const submitTrade = useTradeSubmit()
  const verifyTrade = useTradeVerify()

  const [spreadWarningChecked, setSpreadWarningChecked] = useState(false)
  const usdValueSpreadCalc = useUSDValueSpreadCallback()
  const [usdValueSpread, setUsdValueSpread] = useState('0')
  const usdValueSpreadWarningVisible = useMemo(
    () => Number(usdValueSpread) <= -5,
    [usdValueSpread],
  )

  const disabledByUsdValueUnchecked = useMemo(
    () =>
      usdValueSpreadWarningVisible && isPreviewOpen && !spreadWarningChecked,
    [usdValueSpreadWarningVisible, isPreviewOpen, spreadWarningChecked],
  )
  const disabledByAmount = useMemo(
    () =>
      !inputAmount ||
      isNaN(Number(inputAmount)) ||
      Number(inputAmount) === 0 ||
      !outputAmount ||
      isNaN(Number(outputAmount)) ||
      Number(outputAmount) === 0,
    [inputAmount, outputAmount],
  )
  const disabled = useMemo(() => {
    return (
      !!account &&
      isValidNetwork &&
      !needApprove &&
      (disabledByUsdValueUnchecked || disabledByAmount)
    )
  }, [
    account,
    needApprove,
    isValidNetwork,
    disabledByUsdValueUnchecked,
    disabledByAmount,
  ])

  const onSubmit = useCallback(async () => {
    if (!account) {
      trackClick('webtl_walletconnect', { from: 'limit_order' })
      toggleWalletModal()
      return
    }

    if (!isValidNetwork) {
      toggleSwitchNetworkModal()
      return
    }

    if (isDisableTrade) {
      toggleIpBlockedModal()
      return
    }

    if (isPreviewOpen) {
      trackClick('webtl_lo_preview_confirm')
      if (verifyTrade()) {
        submitTrade()
      }
    } else {
      if (verifyTrade(setTipVisible)) {
        trackClick('webtl_lo_preview', {
          taker_token_symbol: outputToken.symbol,
          maker_token_symbol: inputToken.symbol,
          taker_token_amount: outputAmount,
          maker_token_amount: inputAmount,
          type: expiration,
        })
        togglePreviewModal()
      }
    }
  }, [verifyTrade, submitTrade, isPreviewOpen, isValidNetwork, isDisableTrade])

  const textLabel = useMemo(() => {
    if (!isValidNetwork) {
      return t('switch_network')
    } else if (needApprove) {
      return t('tokenlon_set_allowance', { symbol: inputToken?.symbol })
    } else return text || t('confirm')
  }, [isValidNetwork, needApprove, text, inputToken?.symbol, t])

  useEffect(() => {
    setUsdValueSpread(usdValueSpreadCalc())
    if (!isPreviewOpen) {
      setSpreadWarningChecked(false)
    }
  }, [isPreviewOpen, usdValueSpreadCalc])

  return (
    <>
      {!isValidNetwork && (
        <>
          <Space height={15} />
          <NotificationBar
            level="warning"
            text={t('unmatched_network_swap_warning')}
            showLink={false}
            showClose={false}
          />
        </>
      )}
      {isPreviewOpen && usdValueSpreadWarningVisible ? (
        <SpreadWarningCheck
          usdValueSpread={usdValueSpread}
          checked={spreadWarningChecked}
          setCheck={setSpreadWarningChecked}
        />
      ) : null}
      <StyledTradeButton onClick={onSubmit} disabled={disabled}>
        {account ? textLabel : t('connect')}
        {needApprove && (
          <img className="approve-lock" src={IconLock} alt="lock" />
        )}
      </StyledTradeButton>
    </>
  )
}

export default TradeButton
